/**
 * Categories — clue bank + daily board generator.
 *
 * Pulls from OpenTDB JSON (CC-BY-SA 4.0) staged in
 * `apps/games-web/src/games/trivia/bank/`, transforms each question into a
 * Jeopardy "this/these" clue stem, and assembles 6 categories × 5 rungs of
 * clues into a board. Deterministic given a seed.
 *
 * IP NOTE: J-Archive datasets (and derivatives) cannot be redistributed. We
 * use OpenTDB exclusively. OpenTDB content stays in its own files; we read
 * it, transform it at module-init time, and never bundle a J-Archive clue.
 *
 * Category mapping: 6 categories for the audience.
 *   General Knowledge, History, Geography, Entertainment: TV Classics,
 *   Science & Nature, Sports.
 *
 * Difficulty mapping: a question's $200..$1000 rung is currently a function
 * of its position in the deterministically-shuffled pool — easier values
 * land first. OpenTDB's per-question difficulty is unreliable for this
 * audience (most "easy" Marvel questions are harder than "hard" Carson
 * questions), so we don't trust it. We sort the pool by a simple "stem
 * length + number of digits in answer" complexity proxy and bin into 5
 * buckets. This produces a roughly-monotonic difficulty curve and is good
 * enough for v1.
 */
import OPENTDB_GENERAL from '@/games/trivia/bank/opentdb-general-knowledge.json' with { type: 'json' };
import OPENTDB_GEOGRAPHY from '@/games/trivia/bank/opentdb-geography.json' with { type: 'json' };
import OPENTDB_HISTORY from '@/games/trivia/bank/opentdb-history.json' with { type: 'json' };
import OPENTDB_SCIENCE from '@/games/trivia/bank/opentdb-science.json' with { type: 'json' };
import OPENTDB_SPORTS from '@/games/trivia/bank/opentdb-sports.json' with { type: 'json' };
import OPENTDB_TV from '@/games/trivia/bank/opentdb-tv-classics.json' with { type: 'json' };

import { rewriteToClue } from './stem-rewriter';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BoardCategorySlug =
  | 'general-knowledge'
  | 'history'
  | 'geography'
  | 'tv-classics'
  | 'science'
  | 'sports';

export interface BoardClue {
  /** Stable ID — borrowed from the underlying OpenTDB question. */
  id: string;
  /** The Jeopardy-style stem: "This / These ... " */
  stem: string;
  /** The canonical answer as a Jeopardy "What is X?" / "Who is Y?" string. */
  canonicalAnswer: string;
  /**
   * The bare answer (without "What is" prefix). Used as a fall-back display
   * when revealing. Matching also accepts this directly.
   */
  bareAnswer: string;
  /** Alternates the matcher should accept (synonyms, alternate spellings). */
  acceptableAnswers: ReadonlyArray<string>;
  /** Source question for telemetry. */
  source: 'opentdb';
}

export interface BoardData {
  /** 6 categories, left-to-right across the board. */
  categories: ReadonlyArray<BoardCategorySlug>;
  /** clues[col][row] — col 0..5 = category, row 0..4 = dollar rung (low to high). */
  clues: ReadonlyArray<ReadonlyArray<BoardClue>>;
  /** Daily Double cell coordinates. */
  dailyDoubleCol: number;
  dailyDoubleRow: number;
}

// ---------------------------------------------------------------------------
// Category metadata
// ---------------------------------------------------------------------------

export const CATEGORY_LABELS: Record<BoardCategorySlug, string> = {
  'general-knowledge': 'General Knowledge',
  history: 'History',
  geography: 'Geography',
  'tv-classics': 'TV Classics',
  science: 'Science',
  sports: 'Sports',
};

/** The display order for v1. Six categories — all in. */
export const CATEGORY_ORDER: ReadonlyArray<BoardCategorySlug> = [
  'general-knowledge',
  'history',
  'geography',
  'tv-classics',
  'science',
  'sports',
];

// ---------------------------------------------------------------------------
// Source pools
// ---------------------------------------------------------------------------

interface RawOTDB {
  id: string;
  category: string;
  categorySlug: string;
  prompt: string;
  options: string[];
  correctIndex: number;
  source: 'opentdb';
}

const SOURCE_BY_SLUG: Record<BoardCategorySlug, RawOTDB[]> = {
  'general-knowledge': OPENTDB_GENERAL as RawOTDB[],
  history: OPENTDB_HISTORY as RawOTDB[],
  geography: OPENTDB_GEOGRAPHY as RawOTDB[],
  'tv-classics': OPENTDB_TV as RawOTDB[],
  science: OPENTDB_SCIENCE as RawOTDB[],
  sports: OPENTDB_SPORTS as RawOTDB[],
};

// ---------------------------------------------------------------------------
// PRNG (mulberry32 — same as sudoku for consistency)
// ---------------------------------------------------------------------------

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleInPlace<T>(arr: T[], rng: () => number): void {
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
}

// ---------------------------------------------------------------------------
// Pool transformation cache
// ---------------------------------------------------------------------------

/**
 * Transform a raw OpenTDB question into a BoardClue (Jeopardy-styled).
 * Returns null when the rewriter can't produce a usable clue (e.g. the prompt
 * doesn't parse cleanly).
 */
function toClue(raw: RawOTDB): BoardClue | null {
  const correct = raw.options[raw.correctIndex];
  if (!correct) return null;
  const rewrite = rewriteToClue(raw.prompt, correct);
  if (!rewrite) return null;
  return {
    id: raw.id,
    stem: rewrite.stem,
    canonicalAnswer: rewrite.canonicalAnswer,
    bareAnswer: rewrite.bareAnswer,
    acceptableAnswers: rewrite.acceptableAnswers,
    source: 'opentdb',
  };
}

let TRANSFORMED_POOLS: Record<BoardCategorySlug, BoardClue[]> | null = null;

function getTransformedPools(): Record<BoardCategorySlug, BoardClue[]> {
  if (TRANSFORMED_POOLS) return TRANSFORMED_POOLS;
  const out = {} as Record<BoardCategorySlug, BoardClue[]>;
  for (const slug of CATEGORY_ORDER) {
    const raws = SOURCE_BY_SLUG[slug] ?? [];
    const clues: BoardClue[] = [];
    for (const r of raws) {
      const clue = toClue(r);
      if (clue) clues.push(clue);
    }
    out[slug] = clues;
  }
  TRANSFORMED_POOLS = out;
  return out;
}

// ---------------------------------------------------------------------------
// Complexity proxy — sort the pool roughly by "difficulty"
// ---------------------------------------------------------------------------

function complexityScore(clue: BoardClue): number {
  // Longer stem = harder. Multi-word answer = harder. Number in answer = harder.
  let score = 0;
  score += clue.stem.length * 0.15;
  score += clue.bareAnswer.split(/\s+/).length * 4;
  if (/\d/.test(clue.bareAnswer)) score += 6;
  if (clue.bareAnswer.length > 14) score += 4;
  return score;
}

/** Sort by complexity ascending, then bin into 5 difficulty buckets. */
function bucketByDifficulty(clues: BoardClue[]): BoardClue[][] {
  const sorted = clues.slice().sort((a, b) => complexityScore(a) - complexityScore(b));
  const buckets: BoardClue[][] = [[], [], [], [], []];
  for (let i = 0; i < sorted.length; i += 1) {
    const bucket = Math.min(4, Math.floor((i / sorted.length) * 5));
    buckets[bucket]!.push(sorted[i]!);
  }
  return buckets;
}

// ---------------------------------------------------------------------------
// Board construction
// ---------------------------------------------------------------------------

/** Build one column (5 rungs) from a category pool given a seed. */
function buildColumn(
  slug: BoardCategorySlug,
  pools: Record<BoardCategorySlug, BoardClue[]>,
  rng: () => number,
): BoardClue[] | null {
  const pool = pools[slug];
  if (!pool || pool.length < 10) return null; // need headroom for shuffle
  const buckets = bucketByDifficulty(pool);
  const column: BoardClue[] = [];
  for (let row = 0; row < 5; row += 1) {
    const bucket = buckets[row]!;
    if (bucket.length === 0) {
      // Fall back to whole pool if bucket empty.
      const idx = Math.floor(rng() * pool.length);
      column.push(pool[idx]!);
      continue;
    }
    const idx = Math.floor(rng() * bucket.length);
    column.push(bucket[idx]!);
  }
  return column;
}

export function buildDailyBoard(seed: number): BoardData {
  return buildBoard(seed, /*daily=*/ true);
}

export function buildPracticeBoard(seed: number): BoardData {
  return buildBoard(seed, /*daily=*/ false);
}

function buildBoard(seed: number, _daily: boolean): BoardData {
  const pools = getTransformedPools();
  const rng = mulberry32(seed);

  // For now, every board uses the full 6 categories in canonical order. A
  // future refinement could draw a randomized subset from a wider catalog.
  const categories: BoardCategorySlug[] = [...CATEGORY_ORDER];
  // Light rotation: shift category order by a seed-dependent offset so the
  // top-left tile varies by day (cosmetic only).
  const offset = Math.floor(rng() * categories.length);
  const rotated = [...categories.slice(offset), ...categories.slice(0, offset)];

  // Build each column (a category) with its own sub-rng so a small tweak to a
  // single category's pool doesn't shift every other column.
  const clueColumns: BoardClue[][] = [];
  for (let col = 0; col < 6; col += 1) {
    const slug = rotated[col]!;
    const subSeed = (seed ^ (col * 0x9e3779b1)) >>> 0;
    const subRng = mulberry32(subSeed);
    const column = buildColumn(slug, pools, subRng);
    if (!column) {
      // Severe pool exhaustion — should not happen with the current banks.
      throw new Error(`Categories: pool too small for category ${slug}`);
    }
    clueColumns.push(column);
  }

  // Place Daily Double in a deterministic non-$200 cell.
  const ddCol = Math.floor(rng() * 6);
  const ddRow = 1 + Math.floor(rng() * 4); // rows 1..4 (skip the $200 rung)

  // Shuffle within rotated to vary across days; but keep categories array as
  // the column order we returned.
  shuffleInPlace(rotated, rng);

  // The shuffle above invalidates the clue-column → category mapping. Redo:
  // rebuild columns under the new shuffled order with the same sub-seed
  // pattern so the same category always draws the same column at a given seed.
  const finalCategories: BoardCategorySlug[] = rotated;
  const finalColumns: BoardClue[][] = [];
  for (let col = 0; col < 6; col += 1) {
    const slug = finalCategories[col]!;
    const subSeed = (seed ^ (col * 0x9e3779b1)) >>> 0;
    const subRng = mulberry32(subSeed);
    const column = buildColumn(slug, pools, subRng);
    if (!column) throw new Error(`Categories: pool too small for category ${slug}`);
    finalColumns.push(column);
  }

  return {
    categories: finalCategories,
    clues: finalColumns,
    dailyDoubleCol: ddCol,
    dailyDoubleRow: ddRow,
  };
}

// ---------------------------------------------------------------------------
// Diagnostics — exported for tooling/tests
// ---------------------------------------------------------------------------

export function poolSizes(): Record<BoardCategorySlug, number> {
  const pools = getTransformedPools();
  const out = {} as Record<BoardCategorySlug, number>;
  for (const slug of CATEGORY_ORDER) {
    out[slug] = pools[slug].length;
  }
  return out;
}
